import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  TextField,
  IconButton,
  Chip,
  Stack,
  MenuItem,
  Select,
  Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import authService from '../../../api-authorization/AuthorizeService';

const COUNTRY_OPTIONS = [
  'United States'
];

const US_STATE_OPTIONS = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const UpdateClientDialog = ({ client, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const { handleSubmit, register, setValue, getValues, watch, reset, formState: { errors } } = useForm({
    defaultValues: client
  });

  // Validation patterns
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phonePattern = /^\+?[\d\s\-().]{7,20}$/;
  const zipPattern = /^\d{5}(-\d{4})?$/;
  const alphaPattern = /^[a-zA-Z\s.'-]+$/;

  // Register targetApplicationRates with react-hook-form
  useEffect(() => {
    register('targetApplicationRates');
  }, [register]);

  // Local state for the new rate input
  const [newRate, setNewRate] = useState('');
  // Watch the list for re-render
  const targetApplicationRates = watch('targetApplicationRates', client.targetApplicationRates || []);

  // Validation error state
  const [validationError, setValidationError] = useState('');

  // Populate form when client changes (including targetApplicationRates)
  useEffect(() => {
    if (client) {
      reset(client);
    }
  }, [client, reset]);

  // Add a new rate to the list
  const handleAddRate = () => {
    const parsed = parseInt(newRate, 10);
    if (!isNaN(parsed) && !targetApplicationRates.includes(parsed)) {
      const updated = [...targetApplicationRates, parsed];
      setValue('targetApplicationRates', updated, { shouldValidate: true, shouldDirty: true });
      setNewRate('');
    }
  };

  // Remove a rate from the list
  const handleRemoveRate = (rate) => {
    const updated = targetApplicationRates.filter(r => r !== rate);
    setValue('targetApplicationRates', updated, { shouldValidate: true, shouldDirty: true });
  };

  const validateForm = (data) => {
    if (!Array.isArray(data.targetApplicationRates) || data.targetApplicationRates.length === 0) {
      setValidationError('At least one target application rate is required.');
      return false;
    }
    setValidationError('');
    return true;
  };

  const updateClient = async (data) => {
    if (!validateForm(data)) {
      return;
    }
    const payload = {
      client: {
        id: data.id,
        clientId: data.clientId,
        name: data.name,
        primaryContactEmail: data.primaryContactEmail,
        primaryContactPhone: data.primaryContactPhone,
        targetApplicationRates: data.targetApplicationRates
      },
      location: {
        id: data.locationId,
        clientId: data.clientId,
        name: data.locationName,
        streetAddress: data.streetAddress,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode,
        country: data.country
      }
    };
    const token = await authService.getAccessToken();
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/clients/update`,
        {
          method: 'PUT',
          headers: !token
            ? {}
            : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
          body: JSON.stringify(payload)
        }
      );
      if (response.ok) {
        toast.success('Client updated successfully!');
        onSuccess();
      } else {
        toast.error(`${response.status} ${response.statusText}`);
      }
    } catch (err) {
      toast.error('Error: ' + err);
      console.log('err', err);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(updateClient)}>
        <DialogTitle>
          <EditIcon /> Edit Client
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl>
                  <FormLabel>Client Name</FormLabel>
                  <TextField
                    {...register('name', { required: 'Client name is required.' })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>E-mail</FormLabel>
                  <TextField
                    {...register('primaryContactEmail', {
                      required: 'E-mail is required.',
                      pattern: {
                        value: emailPattern,
                        message: 'Please enter a valid email address.'
                      }
                    })}
                    error={!!errors.primaryContactEmail}
                    helperText={errors.primaryContactEmail?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  <TextField
                    {...register('primaryContactPhone', {
                      required: 'Phone number is required.',
                      pattern: {
                        value: phonePattern,
                        message: 'Please enter a valid phone number.'
                      }
                    })}
                    error={!!errors.primaryContactPhone}
                    helperText={errors.primaryContactPhone?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Target Application Rates</FormLabel>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={newRate}
                      onChange={e => setNewRate(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddRate();
                        }
                      }}
                      label="Add Rate"
                      inputProps={{ min: 0 }}
                      disabled={loading}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleAddRate}
                      disabled={loading || newRate === '' || isNaN(parseInt(newRate, 10)) || targetApplicationRates.includes(parseInt(newRate, 10))}
                      aria-label="add rate"
                      size="large"
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {Array.isArray(targetApplicationRates) && targetApplicationRates.map((rate, idx) => (
                      <Chip
                        key={rate}
                        label={rate}
                        onDelete={() => handleRemoveRate(rate)}
                        color="primary"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl>
                  <FormLabel>Location Name</FormLabel>
                  <TextField
                    {...register('locationName', { required: 'Location name is required.' })}
                    error={!!errors.locationName}
                    helperText={errors.locationName?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Street Address</FormLabel>
                  <TextField
                    {...register('streetAddress', {
                      required: 'Street address is required.',
                      minLength: { value: 3, message: 'Street address must be at least 3 characters.' }
                    })}
                    error={!!errors.streetAddress}
                    helperText={errors.streetAddress?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>City</FormLabel>
                  <TextField
                    {...register('city', {
                      required: 'City is required.',
                      pattern: { value: alphaPattern, message: 'City must contain only letters.' }
                    })}
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>State</FormLabel>
                  <Select defaultValue={client.state || ''} {...register('state', { required: 'State is required.' })}>
                    <MenuItem value="" disabled>Select a state</MenuItem>
                    {US_STATE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Zip Code</FormLabel>
                  <TextField
                    {...register('zipCode', {
                      required: 'Zip code is required.',
                      pattern: { value: zipPattern, message: 'Please enter a valid zip code (e.g. 12345 or 12345-6789).' }
                    })}
                    error={!!errors.zipCode}
                    helperText={errors.zipCode?.message}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Country</FormLabel>
                  <Select defaultValue={client.country || 'United States'} {...register('country')}>
                    {COUNTRY_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
          {validationError && (
            <Box sx={{ mt: 1 }}>
              <span style={{ color: 'red' }}>{validationError}</span>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="outlined" disabled={loading}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateClientDialog;
